import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateLoadingStatus, updatePaymentPopup } from "../RoundToJoin/tocken.slice";
import { paymentBaseURL, paymentGlobalLib } from "../../services/axiosConfig";

const PaymentForm = () => {
  const dispatch = useDispatch();
  const { tokenDetail } = useSelector((state) => state.token);
  const { paymentGTMDetail } = useSelector((state) => state.token);
  const [oid, setOid] = useState(tokenDetail.oid);
  const [uid, setUid] = useState(tokenDetail.uid);
  const [timestamp, setTimestamp] = useState(new Date().toISOString());
  const [errorMessage, setErrorMessage] = useState("");
  const [paymentResponse, setPaymentResponse] = useState(null);


  const modalRef = useRef();
  const initializedRef = useRef(false);

  useEffect(() => {
    if (tokenDetail.oid) {
      setOid(tokenDetail.oid);
      setUid(tokenDetail.uid);
      if (window.SecurePayment && !initializedRef.current) {
        initializedRef.current = true;
        handleInitializePayment();
      }
    }
  }, [tokenDetail]);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = paymentGlobalLib;
    script.async = true;
    script.crossOrigin = "anonymous";

    script.onload = () => {
      if (tokenDetail.oid && tokenDetail.uid && !initializedRef.current) {
        initializedRef.current = true;
        handleInitializePayment();
      }
    };

    script.onerror = () => {
      setErrorMessage("SecurePayment SDK failed to load.");
    };

    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (errorMessage) {
      document
        .getElementById("error-message")
        ?.scrollIntoView({ behavior: "smooth" });
    }
  }, [errorMessage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        dispatch(updatePaymentPopup(false));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dispatch]);

  const handleInitializePayment = () => {
    setErrorMessage("");
    setPaymentResponse(null);

    if (!oid?.trim()) return setErrorMessage("Please provide Order ID");
    if (!uid?.trim()) return setErrorMessage("Please provide User ID");

    const freshTimestamp = new Date().toISOString();
    setTimestamp(freshTimestamp);

    setTimeout(() => {
      try {
        const payment = new window.SecurePayment({
          elementId: "payment-container",
          oid,
          uid,
          timestamp: freshTimestamp,
          styles: {
            container: {
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              padding: "20px",
              maxWidth: "400px",
              margin: "0 auto",
            },
            title: {
              fontSize: "20px",
              fontWeight: "600",
              color: "#844693", // Brand primary color
              marginBottom: "12px",
            },
            subtitle: {
              fontSize: "14px",
              color: "#6a3776", // Brand secondary color
              marginBottom: "20px",
            },
            input: {
              height: "38px",
              padding: "0 12px",
              fontSize: "14px",
              borderWidth: "1px",
              borderStyle: "solid",
              borderColor: "#dddddd",
              borderRadius: "4px",
              backgroundColor: "#ffffff",
              color: "#333333",
              marginBottom: "8px",
              transition: "all 0.2s ease",
              hoverBorderColor: "#844693", // Brand color on hover
              focusBorderColor: "#844693", // Brand color on focus
            },
            label: {
              fontSize: "14px",
              color: "#844693", // Brand color
              fontWeight: "500",
              marginBottom: "6px",
            },
            button: {
              height: "40px",
              backgroundColor: "#844693", // Brand primary color
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "4px",
              padding: "0 16px",
              width: "100%",
              marginTop: "20px",
              cursor: "pointer",
              transition: "background-color 0.2s ease",
              hoverBackgroundColor: "#6a3776", // Brand secondary color
              disabledBackgroundColor: "#e0e0e0",
            },
          },
          baseUrl: paymentBaseURL,
          onSuccess: (response) => {
            setPaymentResponse(response);
            const userInfo = localStorage.getItem("userInfo");
            const userId = userInfo ? JSON.parse(userInfo).userId : null;

            const path = window.location.href;
            const url = new URL(path);
            const oid = url.searchParams.get("oid");
          
            window.dataLayer.push({
              'event': 'purchase_complete',
              'transactionId': paymentGTMDetail?.oid,
              'userId': paymentGTMDetail?.uid,
              'currency': paymentGTMDetail?.currency_code || 'USD',
              'value': Number(paymentGTMDetail.tokenQuantity) * process.env.REACT_APP_PRICE_PER_TOKEN,
              'tokenQuantity': Number(paymentGTMDetail.tokenQuantity)
            });
            dispatch(updatePaymentPopup(false));
          },
          onError: (error) => {
            dispatch(updateLoadingStatus(false));
            setErrorMessage("Payment failed: " + error.message);
          },
        });

        payment.initialize().catch((error) => {
          setErrorMessage("Initialization failed: " + error.message);
        });
      } catch (error) {
        dispatch(updateLoadingStatus(false));
        setErrorMessage("Error: " + error.message);
      }
    }, 100);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex justify-center items-center p-4">
      <div
        ref={modalRef}
        className="bg-white rounded-lg shadow-lg w-full max-w-sm max-h-[90vh] flex flex-col overflow-y-auto relative"
      >
        <button
          onClick={() => dispatch(updatePaymentPopup(false))}
          className="absolute top-2 right-2 text-gray-600 text-2xl hover:text-black"
        >
          &times;
        </button>

        <div className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-center">Secure Payment</h2>

          {errorMessage && (
            <div
              id="error-message"
              className="text-red-500 text-sm text-center"
            >
              {errorMessage}
            </div>
          )}

          {paymentResponse && (
            <div className="text-sm bg-green-100 text-green-800 p-2 rounded">
              <strong>Success Response:</strong>
              <pre className="whitespace-pre-wrap">
                {JSON.stringify(paymentResponse, null, 2)}
              </pre>
            </div>
          )}
          <div id="payment-container" className="mt-4" />
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
